import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, NgZone, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import * as XLSX from 'xlsx';
import {
  AttributionExportItem,
  AttributionItem,
  CartoucheGroupesPayload,
  CartoucheSansGroupeItem,
  CartouchesService,
} from '../../../services/cartouches.service';
import { GroupeAmorces, GroupesAmorcesService } from '../../../services/groupes-amorces.service';

@Component({
  selector: 'app-attribution-analyses',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './attribution.component.html',
  styleUrl: './attribution.component.scss'
})
export class AttributionComponent implements OnInit {
  protected pageMode: 'attribuer' | 'voir' | 'export-excel' = 'attribuer';
  protected cartouchesSansAttribution: CartoucheSansGroupeItem[] = [];
  protected selectedCartoucheIds: number[] = [];
  protected attributions: AttributionItem[] = [];

  protected groupes: GroupeAmorces[] = [];
  protected selectedGroupeIds: number[] = [];

  protected loadingCartouches = false;
  protected loadingGroupes = false;
  protected loadingAttributions = false;
  protected saving = false;
  protected exporting = false;
  protected success = '';
  protected error = '';

  protected filterDateFrom: string = this.toInputDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
  protected filterDateTo: string = this.toInputDate(new Date());

  constructor(
    private readonly cartouchesService: CartouchesService,
    private readonly groupesAmorcesService: GroupesAmorcesService,
    private readonly router: Router,
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.syncPageMode();
    this.router.events.subscribe((event: unknown) => {
      if (event instanceof NavigationEnd) {
        this.syncPageMode();
        this.loadDataForMode();
      }
    });

    this.loadGroupes();
    this.loadDataForMode();
  }

  protected get showAssignSection(): boolean {
    return this.pageMode === 'attribuer';
  }

  protected get showViewSection(): boolean {
    return this.pageMode === 'voir';
  }

  protected get showExportSection(): boolean {
    return this.pageMode === 'export-excel';
  }

  protected get filteredAttributions(): AttributionItem[] {
    const from = this.filterDateFrom ? new Date(this.filterDateFrom + 'T00:00:00') : null;
    const to = this.filterDateTo ? new Date(this.filterDateTo + 'T23:59:59') : null;
    return this.attributions.filter((item) => {
      if (!item.date_attribution) return true;
      const d = new Date(item.date_attribution);
      if (from && d < from) return false;
      if (to && d > to) return false;
      return true;
    });
  }

  protected toInputDate(d: Date): string {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatDateFrench(dateStr: string): string {
    if (!dateStr) {
      return '';
    }
    try {
      const d = new Date(dateStr + 'T00:00:00');
      if (isNaN(d.getTime())) {
        return dateStr;
      }
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dateStr;
    }
  }

  protected isCartoucheChecked(cartoucheId: number): boolean {
    return this.selectedCartoucheIds.includes(cartoucheId);
  }

  protected toggleCartouche(cartoucheId: number, checked: boolean): void {
    if (checked) {
      if (!this.selectedCartoucheIds.includes(cartoucheId)) {
        this.selectedCartoucheIds = [...this.selectedCartoucheIds, cartoucheId];
      }
      return;
    }
    this.selectedCartoucheIds = this.selectedCartoucheIds.filter((id) => id !== cartoucheId);
  }

  protected isGroupeChecked(groupeId: number): boolean {
    return this.selectedGroupeIds.includes(groupeId);
  }

  protected toggleGroupe(groupeId: number, checked: boolean): void {
    if (checked) {
      if (!this.selectedGroupeIds.includes(groupeId)) {
        this.selectedGroupeIds = [...this.selectedGroupeIds, groupeId];
      }
      return;
    }

    this.selectedGroupeIds = this.selectedGroupeIds.filter((id) => id !== groupeId);
  }

  protected submitAttributionsCroisees(): void {
    if (this.selectedCartoucheIds.length === 0) {
      this.error = 'Veuillez sélectionner au moins une cartouche.';
      return;
    }

    if (this.selectedGroupeIds.length === 0) {
      this.error = "Veuillez sélectionner au moins un groupe d'amorces.";
      return;
    }

    this.saving = true;
    this.success = '';
    this.error = '';

    const payload: CartoucheGroupesPayload = {
      groupe_amorce_ids: [...this.selectedGroupeIds].sort((a, b) => a - b),
    };

    this.zone.run(async () => {
      try {
        await Promise.all(
          this.selectedCartoucheIds.map((cartoucheId) =>
            firstValueFrom(this.cartouchesService.updateGroupesAmorces(cartoucheId, payload))
          )
        );
        this.success = 'Attributions enregistrées avec succès.';
        this.selectedCartoucheIds = [];
        this.selectedGroupeIds = [];
        this.loadCartouchesSansAttribution();
      } catch (err: unknown) {
        this.error = `Erreur lors de l'enregistrement: ${this.getErrorMessage(err as HttpErrorResponse)}`;
      } finally {
        this.saving = false;
        this.cdr.markForCheck();
      }
    });
  }

  protected removeAttribution(item: AttributionItem): void {
    if (!confirm(`Supprimer l'attribution de ${item.cartouche_nom} vers ${item.groupe_amorce_nom} ?`)) {
      return;
    }

    this.zone.run(() => {
      this.cartouchesService.deleteAttribution(item.cartouche_id, item.groupe_amorce_id).subscribe({
        next: () => {
          this.success = 'Attribution supprimée avec succès.';
          this.loadAttributions();
          this.loadCartouchesSansAttribution();
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors de la suppression: ${this.getErrorMessage(err)}`;
          this.cdr.markForCheck();
        }
      });
    });
  }

  protected exportExcel(): void {
    this.exporting = true;
    this.error = '';
    this.success = '';

    this.zone.run(() => {
      this.cartouchesService.exportAttributionsData().subscribe({
        next: (items: AttributionExportItem[]) => {
          const exportRows = (items ?? []).map((item) => ({
            'code cartouche': item.code_cartouche,
            "groupe d'amorces": item.groupe_amorces,
            'codes des amorces': item.codes_amorces,
            "date d'attribution": this.formatDate(item.date_attribution),
            'Commande séquençage effectuée': item.sequencage_commande ? 'Oui' : 'Non',
            'Date commande séquençage': item.date_sequencage ? this.formatDateFrench(item.date_sequencage) : '',
          }));

          const worksheet = XLSX.utils.json_to_sheet(exportRows);
          
          // Définir la largeur des colonnes
          worksheet['!cols'] = [
            { wch: 18 }, // code cartouche
            { wch: 25 }, // groupe d'amorces
            { wch: 30 }, // codes des amorces
            { wch: 20 }, // date d'attribution
            { wch: 30 }, // Commande séquençage effectuée
            { wch: 25 }, // Date commande séquençage
          ];

          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, 'Attributions');
          XLSX.writeFile(workbook, 'attributions_cartouches_groupes_amorces.xlsx');

          this.success = 'Export Excel généré avec succès.';
          this.exporting = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors de l'export: ${this.getErrorMessage(err)}`;
          this.exporting = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  private loadGroupes(): void {
    this.loadingGroupes = true;
    this.zone.run(() => {
      this.groupesAmorcesService.list().subscribe({
        next: (groupes: GroupeAmorces[]) => {
          this.groupes = groupes;
          this.loadingGroupes = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors du chargement des groupes d'amorces: ${this.getErrorMessage(err)}`;
          this.groupes = [];
          this.loadingGroupes = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  private loadCartouchesSansAttribution(): void {
    this.loadingCartouches = true;
    this.zone.run(() => {
      this.cartouchesService.listLivreesSansGroupeAmorces().subscribe({
        next: (items: CartoucheSansGroupeItem[]) => {
          this.cartouchesSansAttribution = items;
          this.loadingCartouches = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors du chargement des cartouches: ${this.getErrorMessage(err)}`;
          this.cartouchesSansAttribution = [];
          this.loadingCartouches = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  private loadAttributions(): void {
    this.loadingAttributions = true;
    this.zone.run(() => {
      this.cartouchesService.listAttributions().subscribe({
        next: (items: AttributionItem[]) => {
          this.attributions = items;
          this.loadingAttributions = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors du chargement des attributions: ${this.getErrorMessage(err)}`;
          this.attributions = [];
          this.loadingAttributions = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  private syncPageMode(): void {
    const lastPart = this.router.url.split('?')[0].split('/').pop() ?? '';
    if (['attribuer', 'voir', 'export-excel'].includes(lastPart)) {
      this.pageMode = lastPart as 'attribuer' | 'voir' | 'export-excel';
    } else {
      this.pageMode = 'attribuer';
    }
  }

  private loadDataForMode(): void {
    if (this.pageMode === 'attribuer') {
      this.loadCartouchesSansAttribution();
      return;
    }
    if (this.pageMode === 'voir') {
      this.loadAttributions();
    }
  }

  protected formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) {
      return '—';
    }
    // Accepte YYYY-MM-DD ou ISO datetime complet
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) {
      return dateStr;
    }
    const day = String(d.getUTCDate()).padStart(2, '0');
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const year = d.getUTCFullYear();
    return `${day}/${month}/${year}`;
  }

  private getErrorMessage(err: HttpErrorResponse): string {
    if (typeof err.error === 'string' && err.error.trim()) {
      return err.error;
    }

    if (err.error?.detail !== undefined && err.error?.detail !== null) {
      return this.formatErrorValue(err.error.detail);
    }

    if (err.error?.message !== undefined && err.error?.message !== null) {
      return this.formatErrorValue(err.error.message);
    }

    return err.statusText || 'Erreur inconnue';
  }

  private formatErrorValue(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }

    if (Array.isArray(value)) {
      return value
        .map((item: unknown) => {
          if (typeof item === 'string') {
            return item;
          }
          if (item && typeof item === 'object') {
            const asRecord = item as Record<string, unknown>;
            const msg = asRecord['msg'];
            const loc = asRecord['loc'];
            if (typeof msg === 'string') {
              if (Array.isArray(loc)) {
                return `${msg} (${loc.join(' > ')})`;
              }
              return msg;
            }
          }
          return JSON.stringify(item);
        })
        .join(' | ');
    }

    if (value && typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return '[détail erreur non sérialisable]';
      }
    }

    return String(value);
  }
}
